/**
 * EditorToolbar — renders two toolbar groups via render props:
 *   listToolbar: New, Import, Schedule (parent places above the puzzle list)
 *   boardToolbar: Save, Play, Export JSON + status (parent places above board)
 *
 * Import reads a text file, validates it, creates a new puzzle, and auto-saves.
 * Schedule dialog is rendered as a fixed overlay.
 */

import React, { useRef, useCallback } from 'react';
import type { EditorState, EditorActions } from './useEditorState';
import { importTextToPuzzleJSON } from '../../utils/puzzleParser';
// InviteCodeGenerator removed — beta access now uses email whitelist
import styles from './EditorToolbar.module.css';

interface EditorToolbarProps {
  state: EditorState;
  actions: EditorActions;
  onSaved?: () => void;
  /** Current puzzle status — needed for PLAY button eligibility */
  puzzleStatus?: 'scheduled' | 'ready' | 'core' | 'stub';
  /** Called when the Compress Dates button is clicked */
  onCompressDates?: () => Promise<void>;
  children: (parts: {
    listToolbar: React.ReactNode;
    boardToolbar: React.ReactNode;
  }) => React.ReactNode;
}

/** Derive the right-side caption as React node (supports sample badge) */
function deriveCaption(
  state: EditorState,
  puzzleStatus: string | undefined,
): React.ReactNode {
  const { activePuzzleId, activePuzzleDate, activePuzzleIsSample, isDirty } = state;

  // New unsaved puzzle
  if (!activePuzzleId) {
    return isDirty ? 'Unsaved' : 'New puzzle';
  }

  // Scheduled puzzle — show date, optionally with sample badge
  if (activePuzzleDate) {
    if (activePuzzleIsSample) {
      return (
        <>
          {activePuzzleDate}{' '}
          <svg width={16} height={16} viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
            <circle cx={8} cy={8} r={8} fill="#1f7d7e" />
            <text x={8} y={8} dy=".35em" fill="#f7e5c5" fontFamily="Inter, sans-serif" fontSize={9} fontWeight={700} textAnchor="middle">S</text>
          </svg>
        </>
      );
    }
    return activePuzzleDate;
  }

  // Saved but unscheduled — show status name
  if (puzzleStatus) {
    return puzzleStatus.charAt(0).toUpperCase() + puzzleStatus.slice(1);
  }

  return 'Loaded';
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({ state, actions, onSaved, puzzleStatus, onCompressDates, children }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    const ok = await actions.saveToSupabase();
    if (ok) onSaved?.();
  };

  const handleExport = () => {
    const json = actions.exportJSON();
    navigator.clipboard.writeText(json).then(
      () => alert('JSON copied to clipboard'),
      () => {
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `puzzle-${state.activePuzzleDate ?? 'new'}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    );
  };

  // Play is enabled for Scheduled, Ready, or Past (past = scheduled with date < today)
  const canPlay = puzzleStatus === 'ready' || puzzleStatus === 'scheduled';

  const handlePlay = () => {
    if (!state.activePuzzleId) {
      alert('Save the puzzle first before playing.');
      return;
    }
    if (!canPlay) {
      alert('Only Ready or Scheduled puzzles can be played. Make sure all clues and categories are filled in.');
      return;
    }
    const url = `${window.location.origin}${window.location.pathname}?play=${state.activePuzzleId}`;
    const w = 1100, h = 900;
    const left = Math.round((screen.width - w) / 2);
    const top = Math.round((screen.height - h) / 2);
    window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`);
  };

  /** Import: open file picker */
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /** Import: read selected file, validate, create puzzle, auto-save */
  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      // Validate by attempting to parse — throws on invalid format
      importTextToPuzzleJSON(text);
      // Parse succeeded — import into editor state (creates new puzzle)
      actions.importFromText(text);
      // Auto-save immediately
      const saved = await actions.saveToSupabase();
      if (saved) {
        onSaved?.();
      }
    } catch (err) {
      alert(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [actions, onSaved]);

  const caption = deriveCaption(state, puzzleStatus);

  const listToolbar = (
    <div className={styles.listToolbar}>
      <button className={styles.btn} onClick={actions.newPuzzle}>New</button>
      <button className={styles.btn} onClick={handleImportClick}>Import</button>
      {/* Invite codes removed — beta access now uses email whitelist */}
      {onCompressDates && <button className={styles.btn} onClick={onCompressDates}>Compress Dates</button>}
      {/* Hidden file input for Import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.text"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </div>
  );

  const boardToolbar = (
    <div className={styles.boardToolbar}>
      <button
        className={state.isDirty ? styles.btnActive : styles.btn}
        onClick={handleSave}
        style={state.isDirty ? undefined : { opacity: 0.4 }}
      >
        Save
      </button>
      <button
        className={canPlay ? styles.btnActive : styles.btn}
        onClick={handlePlay}
        title={canPlay ? 'Open a playable test version' : 'Only Ready or Scheduled puzzles can be played'}
        style={canPlay ? undefined : { opacity: 0.4 }}
      >
        Play
      </button>
      <button className={styles.btn} onClick={handleExport}>Export JSON</button>
      <div className={styles.status}>
        {state.isDirty && <span className={styles.dirtyDot} />}
        {caption}
      </div>
    </div>
  );

  return (
    <>
      {children({ listToolbar, boardToolbar })}
    </>
  );
};

export default React.memo(EditorToolbar);
